/**
 * <em-quick-buy> — quick add to cart from the Erika Morón product card.
 * Posts the card form to the Ajax cart, then announces the change through Horizon's standard
 * cart event so the header bubble and the cart drawer refresh, and opens the drawer.
 * Without JavaScript the form still posts to /cart/add.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

const jsonHeaders = { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' };

function cartRoutes() {
  const routes = window.Theme?.routes ?? {};
  const root = window.Shopify?.routes?.root ?? '/';
  return {
    add: routes.cart_add_url ?? `${root}cart/add.js`,
    cart: routes.cart_url ?? `${root}cart`,
  };
}

class EmQuickBuy extends HTMLElement {
  #busy = false;

  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
    this.removeEventListener('click', this.#onClick);
  }

  get dialog() {
    return this.querySelector('dialog');
  }

  #onClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    if (target.closest('[data-em-quick-buy-open]')) {
      event.preventDefault();
      this.dialog?.showModal();
      return;
    }

    if (target.closest('[data-em-quick-buy-close]') || target === this.dialog) {
      this.dialog?.close();
    }
  };

  #onSubmit = async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('em-quick-buy__form')) return;

    event.preventDefault();
    if (this.#busy) return;
    this.#busy = true;

    const submitter = /** @type {HTMLElement | null} */ (event.submitter ?? form.querySelector('[type="submit"]'));
    submitter?.setAttribute('aria-busy', 'true');
    this.#setError('');

    const { add, cart } = cartRoutes();
    const formData = new FormData(form);
    const quantity = Number(formData.get('quantity')) || 1;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const deferred = CartLinesUpdateEvent.createPromise();
    const inDialog = Boolean(this.dialog?.open);
    if (inDialog) this.dialog?.close();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(formData.get('id')), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(add, { method: 'POST', headers: jsonHeaders, body: formData });
      const result = await response.json();
      const ajaxCart = await fetch(`${cart}.js`, { headers: jsonHeaders }).then((r) => r.json());

      const failed = Boolean(result.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'em-quick-buy',
          sourceId: this.closest('[data-product-id]')?.getAttribute('data-product-id') ?? '',
          itemCount: quantity,
          productId: this.closest('[data-product-id]')?.getAttribute('data-product-id') ?? '',
          sections: failed ? undefined : result.sections,
          didError: failed,
        },
      });

      if (failed) {
        this.#setError(result.description || result.message || '');
        return;
      }

      this.#revealCart(cart);
    } catch (error) {
      deferred.reject(error);
      form.submit();
    } finally {
      this.#busy = false;
      submitter?.removeAttribute('aria-busy');
    }
  };

  #revealCart(cartUrl) {
    const drawer = /** @type {(HTMLElement & { open?: () => void, isOpen?: boolean }) | null} */ (
      document.querySelector('theme-drawer#cart-drawer')
    );
    const autoOpens = document.querySelector('cart-drawer-component[auto-open]');

    if (drawer && typeof drawer.open === 'function') {
      if (!autoOpens && !drawer.isOpen) drawer.open();
      return;
    }

    if (!document.querySelector('cart-items-component')) {
      window.location.href = cartUrl;
    }
  }

  #setError(message) {
    const node = this.querySelector('.em-quick-buy__error');
    if (!node) return;
    node.textContent = message;
    node.hidden = !message;
    if (message) setTimeout(() => (node.hidden = true), 6000);
  }
}

if (!customElements.get('em-quick-buy')) {
  customElements.define('em-quick-buy', EmQuickBuy);
}
